import type { RailBranch, RailLayout } from "@/data/rail-branches";
import type { LocationRun } from "./runs";
import { markerRailPose } from "../../rail/RailPose/pose";
import { locationStackTopYBelowMarkerPose } from "../LocationLayout/metrics";
import { partRunPhase } from "../../rail/PartRunPhase/phase";
import { railCurveMs } from "../../rail/RailLanes/lanes";
import { computeSceneMotion } from "@/lib/timeline/scene/SceneMotion/motion";
import {
  branchForLocationRun,
  forkTravelerCharacterId,
  partOpensAtBranchFork,
} from "./siteBranches";
import { siteRunFollowsCastColumn } from "./siteCastColumn";

/** Pose time for fork handoff: parked at split until the curve, then live playhead. */
export function forkHandoffPoseMs(
  branch: RailBranch,
  playheadMs: number,
): number {
  if (playheadMs < branch.startMs) {
    return branch.startMs;
  }
  return playheadMs;
}

function forkEntryFollowWindow(branch: RailBranch, playheadMs: number): boolean {
  const curveMs = railCurveMs();
  return playheadMs < branch.startMs + curveMs;
}

/** Rejoin curve at branch end (through branch endMs, not onto the main line). */
export function forkRejoinFollowWindow(
  branch: RailBranch,
  playheadMs: number,
): boolean {
  if (branch.joinToLane === undefined) {
    return false;
  }
  const curveMs = railCurveMs();
  return playheadMs >= branch.endMs - curveMs && playheadMs < branch.endMs;
}

export function forkBranchRejoinShowsSite(
  rail: RailLayout,
  branchId: string,
  playheadMs: number,
): boolean {
  const branch = rail.branches.find((item) => item.id === branchId);
  if (branch === undefined) {
    return false;
  }
  return forkRejoinFollowWindow(branch, playheadMs);
}

function forkTravelerFollowOnCurve(
  rail: RailLayout,
  run: LocationRun,
  branch: RailBranch,
  playheadMs: number,
  entry: boolean,
): boolean {
  if (run.runId.includes(":")) {
    if (entry) {
      if (!partOpensAtBranchFork(rail, branch, run)) {
        return false;
      }
      const phase = partRunPhase(rail, run, playheadMs);
      return phase.kind === "upcoming" || phase.kind === "active";
    }
    const sampleMs = playheadMs < branch.endMs ? playheadMs : branch.endMs - 1;
    return partRunPhase(rail, run, sampleMs).kind === "active";
  }
  if (entry) {
    return playheadMs < run.endMs;
  }
  if (playheadMs < branch.endMs) {
    return playheadMs < run.endMs;
  }
  return siteRunFollowsCastColumn(rail, run, branch.endMs - 1);
}

function forkTravelerFollowInterior(
  rail: RailLayout,
  run: LocationRun,
  playheadMs: number,
): boolean {
  if (run.runId.includes(":")) {
    return partRunPhase(rail, run, playheadMs).kind === "active";
  }
  if (playheadMs >= run.startMs && playheadMs < run.endMs) {
    return true;
  }
  return siteRunFollowsCastColumn(rail, run, playheadMs);
}

/** Traveler pose for fork entry, branch interior, and rejoin (one continuous ride). */
export function forkTravelerFollowActive(
  rail: RailLayout,
  run: LocationRun,
  playheadMs: number,
): boolean {
  const branch = branchForLocationRun(run, rail);
  if (branch === undefined || (branch.lane ?? 0) === 0) {
    return false;
  }
  if (forkTravelerCharacterId(rail, branch) === undefined) {
    return false;
  }
  const curveMs = railCurveMs();
  const entry = forkEntryFollowWindow(branch, playheadMs);
  const rejoin = forkRejoinFollowWindow(branch, playheadMs);
  if (entry || rejoin) {
    return forkTravelerFollowOnCurve(rail, run, branch, playheadMs, entry);
  }
  const interiorStart = branch.startMs + curveMs;
  if (playheadMs < interiorStart || playheadMs >= branch.endMs) {
    return false;
  }
  return forkTravelerFollowInterior(rail, run, playheadMs);
}

export function forkTravelerStageAnchor(
  rail: RailLayout,
  branch: RailBranch,
  playheadMs: number,
  viewportWidth: number,
  totalDurationMs: number,
  topYDelta = 0,
): { anchorX: number; topY: number } | null {
  const characterId = forkTravelerCharacterId(rail, branch);
  if (characterId === undefined) {
    return null;
  }
  const poseMs = forkHandoffPoseMs(branch, playheadMs);
  // Everyone on this rail, so boxes sit centered below a whole group, not under one person.
  const onRail = new Set([characterId]);
  for (const stay of rail.stays) {
    if (stay.rootPlaceId === branch.rootPlaceId && poseMs >= stay.startMs && poseMs < stay.endMs) {
      onRail.add(stay.characterId);
    }
  }
  const poses = [...onRail].flatMap((id) => markerRailPose(id, poseMs, rail) ?? []);
  if (poses.length === 0) {
    return null;
  }
  const dxs = poses.map((pose) => pose.dx);
  const lowest = poses.reduce((low, pose) => (
    pose.railDy + pose.dy > low.railDy + low.dy ? pose : low
  ));
  const anchorX = computeSceneMotion(
    poseMs,
    viewportWidth,
    totalDurationMs,
  ).groupCenterX + (Math.min(...dxs) + Math.max(...dxs)) / 2;
  const topY = locationStackTopYBelowMarkerPose(lowest) + topYDelta;
  return { anchorX, topY };
}
